import { useState, useEffect, useCallback } from "react";
import { Activity, Database, ShieldAlert, Mail, Clock, RefreshCw, Ban } from "lucide-react";
import { getHealthSummary, getLoginErrors, pingHealth } from "@/lib/api";

const STATUS_META = {
  healthy: { label: "Healthy", dot: "bg-green-500", ring: "ring-green-500/30", text: "text-green-700", bg: "bg-green-50" },
  degraded: { label: "Degraded", dot: "bg-amber-500", ring: "ring-amber-500/30", text: "text-amber-700", bg: "bg-amber-50" },
  down: { label: "Down", dot: "bg-red-500", ring: "ring-red-500/30", text: "text-red-700", bg: "bg-red-50" },
  unknown: { label: "Unknown", dot: "bg-gray-400", ring: "ring-gray-400/30", text: "text-gray-600", bg: "bg-gray-50" },
};

const REASON_META = {
  invalid_password: { label: "Wrong password", cls: "bg-amber-100 text-amber-800" },
  unknown_account: { label: "Unknown email", cls: "bg-orange-100 text-orange-800" },
  account_locked: { label: "Locked", cls: "bg-red-100 text-red-800" },
  banned: { label: "Banned", cls: "bg-red-100 text-red-800" },
};

function fmtUptime(sec) {
  if (sec == null) return "—";
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid={`health-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-gray-500 text-xs font-semibold uppercase tracking-wide">
        <Icon className={`w-4 h-4 ${accent || "text-[#d4af37]"}`} />
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function SystemHealthPage() {
  const [summary, setSummary] = useState(null);
  const [errors, setErrors] = useState([]);
  const [pingMs, setPingMs] = useState(null);
  const [pingOk, setPingOk] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const t0 = performance.now();
      const [ping, sum, errs] = await Promise.allSettled([pingHealth(), getHealthSummary(), getLoginErrors(50)]);
      if (ping.status === "fulfilled") { setPingOk(true); setPingMs(Math.round(performance.now() - t0)); }
      else { setPingOk(false); setPingMs(null); }
      if (sum.status === "fulfilled") setSummary(sum.value.data);
      if (errs.status === "fulfilled") setErrors(errs.value.data.errors || []);
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  const status = pingOk === false ? "down" : (summary?.status || "unknown");
  const meta = STATUS_META[status] || STATUS_META.unknown;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="system-health-page">
      {/* Header + status badge */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#d4af37]" /> System Health
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Live origin & login watchdog</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ring-2 ${meta.ring} ${meta.bg}`} data-testid="health-status-badge">
            <span className={`w-2.5 h-2.5 rounded-full ${meta.dot} ${status !== "down" ? "animate-pulse" : ""}`}></span>
            <span className={`font-semibold text-sm ${meta.text}`}>{meta.label}</span>
          </div>
          <button onClick={load} data-testid="health-refresh-btn" className="p-2 rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition-colors" title="Refresh now">
            <RefreshCw className={`w-4 h-4 text-gray-600 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Activity} label="Origin ping" value={pingOk === false ? "Unreachable" : (pingMs != null ? `${pingMs} ms` : "—")} sub="Frontend → backend" accent={pingOk === false ? "text-red-500" : "text-green-500"} />
        <StatCard icon={Database} label="DB latency" value={summary ? `${summary.db_latency_ms} ms` : "—"} sub={summary ? (summary.db_ok ? "Connected" : "Unreachable") : ""} accent={summary?.db_ok === false ? "text-red-500" : "text-green-500"} />
        <StatCard icon={Clock} label="Uptime" value={fmtUptime(summary?.uptime_seconds)} sub="Since last restart" />
        <StatCard icon={ShieldAlert} label="Failed logins" value={summary?.failed_logins_1h ?? "—"} sub={`last hour · ${summary?.failed_logins_24h ?? "—"} in 24h`} accent="text-orange-500" />
        <StatCard icon={Mail} label="Email outbox" value={summary?.email_outbox_pending ?? "—"} sub="pending / retrying" accent="text-blue-500" />
        <StatCard icon={Ban} label="Active bans" value={summary?.active_bans ?? "—"} sub="IPs & emails blocked" accent="text-red-500" />
      </div>

      {/* Recent login errors */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-orange-500" /> Recent login errors</h2>
          {lastRefresh && <span className="text-xs text-gray-400">Updated {lastRefresh.toLocaleTimeString("en-GB")}</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="login-errors-table">
            <thead>
              <tr className="text-left text-gray-400 text-xs uppercase tracking-wide bg-gray-50">
                <th className="px-4 py-2 font-medium">Time (UTC)</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">IP</th>
                <th className="px-4 py-2 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {errors.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No recent login errors 🎉</td></tr>
              ) : errors.map((e, i) => {
                const rm = REASON_META[e.reason] || { label: e.reason, cls: "bg-gray-100 text-gray-700" };
                return (
                  <tr key={e.id || i} className="border-t border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{(e.attempt_time || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="px-4 py-2 text-gray-800 font-medium">{e.email || "—"}</td>
                    <td className="px-4 py-2 text-gray-500 font-mono text-xs">{e.ip_address || "—"}</td>
                    <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${rm.cls}`}>{rm.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
